import { useCallback, useMemo } from 'react';
import { useActivePackages } from './useActivePackages';
import { packageOptionLabel } from './planCategory';
import { PlatformAccessPackagePlan } from '../constants/index';

export const PLATFORM_ACCESS_UUID = PlatformAccessPackagePlan[0].value;
// The Platform Access plan is always billed in 4-week cycles, so its number of
// weeks is fixed at 4 and cannot be changed when it is the selected target.
export const PLATFORM_ACCESS_WEEKS = '4';

const WEEKS_OPTIONS = [4, 12, 24, 52];

export interface DowngradePackageOption {
  value: string;
  label: string;
  sessionsPerWeek: number;
  // PROD-9767: the dropdown groups by this; the label is the bare package name.
  planCategory?: string | null;
  fullLabel?: string;
}

export interface UseDowngradeOptionsParams {
  /** UUID of the plan the student is currently enrolled to. */
  student_plan_uuid: string;
  /** UUID of the plan currently selected in the dropdown. */
  selectedPackageUUID: string;
  is_prepaid: number;
  /** Subscribed (auto-renewing) prepaid students; absent on flows that do not pass it. */
  is_subscribed?: number;
  no_of_weeks_originally_enrolled_to: number;
}

export const buildDowngradePlanOptions = ({
  allPackages,
  currentTier,
  is_prepaid
}: {
  allPackages: DowngradePackageOption[];
  currentTier: number;
  is_prepaid: number;
}) => {
  if (currentTier === -1) return allPackages;
  if (is_prepaid === 1) return allPackages;
  return allPackages.filter((pkg) => {
    if (pkg.value === PLATFORM_ACCESS_UUID) return true;
    return pkg.sessionsPerWeek !== -1 && pkg.sessionsPerWeek <= currentTier;
  });
};

// Allowed billing-cadence (weeks) options: a lower-tier target is itself the
// downgrade so any cadence is allowed (PROD-7427); otherwise the cadence must shrink.
export const buildDowngradeWeeksOptions = ({
  selectedPackageUUID,
  student_plan_uuid,
  is_prepaid,
  is_subscribed,
  currentTier,
  selectedTier,
  no_of_weeks_originally_enrolled_to
}: {
  selectedPackageUUID: string;
  student_plan_uuid: string;
  is_prepaid: number;
  is_subscribed: number;
  currentTier: number;
  selectedTier: number;
  no_of_weeks_originally_enrolled_to: number;
}): number[] => {
  if (selectedPackageUUID === PLATFORM_ACCESS_UUID) {
    return [Number(PLATFORM_ACCESS_WEEKS)];
  }
  const currentWeeks = Number(no_of_weeks_originally_enrolled_to || 0);
  if (is_prepaid === 1 && currentWeeks) {
    const tiersKnown = currentTier !== -1 && selectedTier !== -1;
    const movingUpTier = tiersKnown && selectedTier > currentTier;
    const movingDownTier = tiersKnown && selectedTier < currentTier;
    const samePlan = selectedPackageUUID === student_plan_uuid;
    if (movingDownTier) {
      return WEEKS_OPTIONS;
    }
    if (movingUpTier || (is_subscribed === 1 && samePlan)) {
      return WEEKS_OPTIONS.filter((w) => w < currentWeeks);
    }
    if (is_subscribed === 1) {
      return WEEKS_OPTIONS.filter((w) => w <= currentWeeks);
    }
  }
  return WEEKS_OPTIONS;
};

/**
 * Single source of truth for the membership-downgrade option lists shared by the
 * Schedule ("Downgrademembership") and Immediate ("ImmediateDowngrade") flows.
 *
 * Plan tier is derived from the package's `sessions_per_week` (provided by the
 * active_packages API) rather than a hardcoded UUID list, so newer plans such as
 * the *-HIGH-VOLUME tiers resolve to a real tier instead of -1. Both the
 * selectable target plans and the allowed number-of-weeks are computed here so
 * the two modals can never drift apart.
 */
export const useDowngradeOptions = ({
  student_plan_uuid,
  selectedPackageUUID,
  is_prepaid,
  is_subscribed = 0,
  no_of_weeks_originally_enrolled_to
}: UseDowngradeOptionsParams) => {
  const { packages, error } = useActivePackages();

  const allPackages = useMemo<DowngradePackageOption[]>(
    () =>
      packages.map((p) => ({
        value: p.uuid,
        label: p.name,
        sessionsPerWeek: p.sessions_per_week,
        planCategory: p.plan_category,
        fullLabel: packageOptionLabel(p)
      })),
    [packages]
  );

  const getPlanTier = useCallback(
    (uuid: string) => {
      const pkg = allPackages.find((p) => p.value === uuid);
      return pkg ? pkg.sessionsPerWeek : -1;
    },
    [allPackages]
  );

  const currentTier = getPlanTier(student_plan_uuid);
  const selectedTier = getPlanTier(selectedPackageUUID);

  const isPlatformAccessSelected = selectedPackageUUID === PLATFORM_ACCESS_UUID;

  // Selectable target plans: an unknown current tier or a prepaid student can
  // pick any plan; otherwise only plans at or below the current tier. Platform
  // Access is always allowed as a special downgrade target even though its
  // sessions_per_week metadata does not sort below every current plan.
  const planOptions = useMemo(
    () =>
      buildDowngradePlanOptions({
        allPackages,
        currentTier,
        is_prepaid
      }),
    [currentTier, allPackages, is_prepaid]
  );

  // Allowed number-of-weeks (billing cadence) options — see buildDowngradeWeeksOptions.
  // A lower-tier target is itself the downgrade, so its cadence is unrestricted
  // (incl. moving to a longer 24w/52w membership on the cheaper plan, PROD-7427).
  const weeksOptions = useMemo(
    () =>
      buildDowngradeWeeksOptions({
        selectedPackageUUID,
        student_plan_uuid,
        is_prepaid,
        is_subscribed,
        currentTier,
        selectedTier,
        no_of_weeks_originally_enrolled_to
      }),
    [
      is_prepaid,
      is_subscribed,
      selectedTier,
      currentTier,
      selectedPackageUUID,
      student_plan_uuid,
      no_of_weeks_originally_enrolled_to
    ]
  );

  const getPackageName = useCallback(
    (uuid: string) => allPackages.find((p) => p.value === uuid)?.fullLabel || uuid,
    [allPackages]
  );

  return {
    allPackages,
    planOptions,
    weeksOptions,
    currentTier,
    selectedTier,
    isPlatformAccessSelected,
    getPackageName,
    error
  };
};
